import React, { useState } from 'react';
import { showToast } from '../components/Toast';
import ConfirmModal from '../components/ConfirmModal';
import StatusBadge from '../components/StatusBadge';
import BatteryMeter from '../components/BatteryMeter';
import CompetitionBottomNav from '../components/CompetitionBottomNav';
import { TEAMS, getTeamMeta, getDisplayName, formatScoreAbbreviated, formatPercent, TeamIcon } from '../utils/transformersCompetition';
import { useCompetitionAdmin } from '../hooks/useCompetitionAdmin';
import { formatRecapText } from '../utils/transformersRecap';
import './transformersCompetitionShared.css';

const TABS = [
  { key: 'roster', label: 'Roster' },
  { key: 'teams', label: 'Team Standings' },
  { key: 'history', label: 'Week History' }
];

// There is only ever one Transformers season, so this page is the whole admin surface for
// it - season setup when none exists yet, weekly operations once it does. No season list,
// no switching between seasons, nothing to navigate "back" to.
function CompetitionAdmin() {
  const {
    competition,
    players,
    standings,
    rounds,
    loading,
    error,
    eligibleCount,
    scheduledWeeks,
    weeklyRecap,
    createCompetition,
    startSeason,
    saveSchedule,
    removeScheduledWeek,
    saveWeekEdit,
    saveAverage,
    deleteCompetition,
    syncRound,
    closeWeek,
    saveOverride,
    removePlayer,
    resolveTie
  } = useCompetitionAdmin();

  const [activeTab, setActiveTab] = useState('roster');
  const [sortByBatteryAsc, setSortByBatteryAsc] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const [showCreateSheet, setShowCreateSheet] = useState(false);
  const [newName, setNewName] = useState('Transformers Pinball Championship');
  const [newDescription, setNewDescription] = useState('');
  const [creating, setCreating] = useState(false);

  const [showStartSheet, setShowStartSheet] = useState(false);
  const [sternEventCode, setSternEventCode] = useState('');
  const [machineAverageScore, setMachineAverageScore] = useState('');
  const [starting, setStarting] = useState(false);

  const [showCloseSheet, setShowCloseSheet] = useState(false);
  const [nextEventCode, setNextEventCode] = useState('');
  const [closing, setClosing] = useState(false);

  const [showScheduleSheet, setShowScheduleSheet] = useState(false);
  const [scheduleCodesText, setScheduleCodesText] = useState('');
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [editingWeekNumber, setEditingWeekNumber] = useState(null);
  const [editingWeekCode, setEditingWeekCode] = useState('');
  const [savingWeekEdit, setSavingWeekEdit] = useState(false);

  const [showAverageSheet, setShowAverageSheet] = useState(false);
  const [averageValue, setAverageValue] = useState('');
  const [savingAverage, setSavingAverage] = useState(false);

  const [overridePlayer, setOverridePlayer] = useState(null);
  const [overrideBatteryDelta, setOverrideBatteryDelta] = useState('0');
  const [overrideRustDelta, setOverrideRustDelta] = useState('0');
  const [overrideForceLock, setOverrideForceLock] = useState('');
  const [savingOverride, setSavingOverride] = useState(false);

  const [showTieBreakSheet, setShowTieBreakSheet] = useState(false);
  const [tieBreakWinnerId, setTieBreakWinnerId] = useState('');
  const [tieBreakNote, setTieBreakNote] = useState('');
  const [resolvingTie, setResolvingTie] = useState(false);

  const [recapCopied, setRecapCopied] = useState(false);

  const [confirmState, setConfirmState] = useState(null);
  const [confirmSubmitting, setConfirmSubmitting] = useState(false);

  const currentRound = rounds.find(r => r.id === competition?.current_round_id);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      setCreating(true);
      await createCompetition(newName, newDescription);
      setShowCreateSheet(false);
      setNewDescription('');
    } catch (err) {
      showToast('Failed to create competition: ' + (err.response?.data?.error || err.message), 'error');
    } finally {
      setCreating(false);
    }
  };

  const handleStartSeason = async (e) => {
    e.preventDefault();
    if (!competition) return;
    try {
      setStarting(true);
      await startSeason(competition.id, {
        sternEventCode,
        machineAverageScore: parseFloat(machineAverageScore)
      });
      setShowStartSheet(false);
    } catch (err) {
      showToast('Failed to start season: ' + (err.response?.data?.error || err.message), 'error');
    } finally {
      setStarting(false);
    }
  };

  const handleSaveSchedule = async (e) => {
    e.preventDefault();
    if (!competition) return;
    const codes = scheduleCodesText.split('\n').map(c => c.trim());
    if (codes.every(c => !c)) return;
    try {
      setSavingSchedule(true);
      await saveSchedule(competition.id, codes);
      setShowScheduleSheet(false);
      setScheduleCodesText('');
    } catch (err) {
      showToast('Failed to save the schedule: ' + (err.response?.data?.error || err.message), 'error');
    } finally {
      setSavingSchedule(false);
    }
  };

  const handleRemoveScheduledWeek = async (roundNumber) => {
    if (!competition) return;
    try {
      await removeScheduledWeek(competition.id, roundNumber);
    } catch (err) {
      showToast('Failed to remove that week: ' + (err.response?.data?.error || err.message), 'error');
    }
  };

  const startEditingWeek = (week) => {
    setEditingWeekNumber(week.roundNumber);
    setEditingWeekCode(week.sternEventCode);
  };

  const handleSaveWeekEdit = async (e) => {
    e.preventDefault();
    if (!competition || !editingWeekNumber || !editingWeekCode.trim()) return;
    try {
      setSavingWeekEdit(true);
      await saveWeekEdit(competition.id, editingWeekNumber, editingWeekCode.trim());
      setEditingWeekNumber(null);
      setEditingWeekCode('');
    } catch (err) {
      showToast('Failed to update that week: ' + (err.response?.data?.error || err.message), 'error');
    } finally {
      setSavingWeekEdit(false);
    }
  };

  const openSternLeaderboard = (week) => {
    window.open(`https://insider.sternpinball.com/ic/leaderboards/${week.sternEventCode}?game=TFG`, '_blank', 'noopener');
  };

  const openAverageSheet = () => {
    setAverageValue(currentRound?.machine_average_score ? String(Math.round(parseFloat(currentRound.machine_average_score))) : '');
    setShowAverageSheet(true);
  };

  const handleSaveAverage = async (e) => {
    e.preventDefault();
    if (!competition || !currentRound) return;
    try {
      setSavingAverage(true);
      await saveAverage(competition.id, currentRound.id, parseFloat(averageValue));
      setShowAverageSheet(false);
    } catch (err) {
      showToast('Failed to update the average: ' + (err.response?.data?.error || err.message), 'error');
    } finally {
      setSavingAverage(false);
    }
  };

  const tvUrl = competition ? `${window.location.origin}/competitions/${competition.id}/tv` : '';

  const handleOpenTv = () => {
    window.open(tvUrl, '_blank', 'noopener');
  };

  const handleCopyTvLink = async () => {
    try {
      await navigator.clipboard.writeText(tvUrl);
      showToast('TV dashboard link copied to clipboard.', 'success');
    } catch (err) {
      // eslint-disable-next-line no-alert
      window.prompt('Copy this link:', tvUrl);
    }
  };

  const handleCopyRecap = async () => {
    if (!weeklyRecap) return;
    const text = formatRecapText(weeklyRecap);
    try {
      await navigator.clipboard.writeText(text);
      setRecapCopied(true);
      setTimeout(() => setRecapCopied(false), 2500);
    } catch (err) {
      // eslint-disable-next-line no-alert
      window.prompt('Copy this text:', text);
    }
  };

  const handleDelete = () => {
    if (!competition) return;
    setConfirmState({
      title: 'Reset Competition?',
      message: 'Delete ALL competition data (including any other/older seasons found) and start fresh? This cannot be undone.',
      confirmLabel: 'Delete Everything',
      danger: true,
      onConfirm: async () => {
        try {
          setConfirmSubmitting(true);
          await deleteCompetition(competition.id);
          setConfirmState(null);
        } catch (err) {
          showToast('Failed to delete competition: ' + (err.response?.data?.error || err.message), 'error');
        } finally {
          setConfirmSubmitting(false);
        }
      }
    });
  };

  const handleSync = async () => {
    if (!currentRound) return;
    try {
      setSyncing(true);
      await syncRound(competition.id, currentRound.id);
    } catch (err) {
      showToast('Sync failed: ' + (err.response?.data?.error || err.message), 'error');
    } finally {
      setSyncing(false);
    }
  };

  const handleCloseWeek = async (e) => {
    e.preventDefault();
    if (!currentRound) return;
    try {
      setClosing(true);
      const result = await closeWeek(competition.id, currentRound.id, nextEventCode);
      setShowCloseSheet(false);
      setNextEventCode('');
      if (result.seasonEnded) {
        showToast(
          result.grandChampionUserId
            ? 'The season has ended - a Grand Champion has been crowned!'
            : 'The season has ended in an unresolved tie. Use the "Resolve Grand Champion Tie" action to declare a winner.',
          result.grandChampionUserId ? 'success' : 'warning'
        );
      }
    } catch (err) {
      showToast('Failed to close the week: ' + (err.response?.data?.error || err.message), 'error');
    } finally {
      setClosing(false);
    }
  };

  const openOverride = (player) => {
    setOverridePlayer(player);
    setOverrideBatteryDelta('0');
    setOverrideRustDelta('0');
    setOverrideForceLock('');
  };

  const handleSaveOverride = async (e) => {
    e.preventDefault();
    if (!overridePlayer) return;
    try {
      setSavingOverride(true);
      await saveOverride(competition.id, overridePlayer.userId || overridePlayer.sternUsername, {
        batteryDelta: parseFloat(overrideBatteryDelta || '0'),
        rustDelta: parseInt(overrideRustDelta || '0', 10),
        forceStasisLock: overrideForceLock === '' ? undefined : overrideForceLock === 'true'
      });
      setOverridePlayer(null);
    } catch (err) {
      showToast('Failed to update player: ' + (err.response?.data?.error || err.message), 'error');
    } finally {
      setSavingOverride(false);
    }
  };

  const handleRemovePlayer = (player) => {
    setConfirmState({
      title: 'Remove Player?',
      message: `Remove ${getDisplayName(player)} from this competition?`,
      confirmLabel: 'Remove',
      danger: true,
      onConfirm: async () => {
        try {
          setConfirmSubmitting(true);
          await removePlayer(competition.id, player.userId || player.sternUsername);
          setConfirmState(null);
        } catch (err) {
          showToast('Failed to remove player: ' + (err.response?.data?.error || err.message), 'error');
        } finally {
          setConfirmSubmitting(false);
        }
      }
    });
  };

  const handleResolveTie = async (e) => {
    e.preventDefault();
    if (!tieBreakWinnerId) return;
    const isStern = tieBreakWinnerId.startsWith('stern:');
    try {
      setResolvingTie(true);
      await resolveTie(competition.id, {
        winnerUserId: isStern ? undefined : parseInt(tieBreakWinnerId, 10),
        winnerSternUsername: isStern ? tieBreakWinnerId.slice('stern:'.length) : undefined,
        note: tieBreakNote || undefined
      });
      setShowTieBreakSheet(false);
    } catch (err) {
      showToast('Failed to declare Grand Champion: ' + (err.response?.data?.error || err.message), 'error');
    } finally {
      setResolvingTie(false);
    }
  };

  if (loading) return <div className="loading">Loading...</div>;

  const sortedPlayers = [...players].sort((a, b) => (sortByBatteryAsc
    ? a.energonBattery - b.energonBattery
    : a.team.localeCompare(b.team) || getDisplayName(a).localeCompare(getDisplayName(b))));

  // Keyed by user_id when claimed, Stern username when not - a player doesn't need a linked
  // account to show up here.
  const liveScoresByPlayerKey = {};
  (standings?.teams || []).forEach(team => {
    team.members.forEach(m => { liveScoresByPlayerKey[m.userId || m.sternUsername] = m; });
  });

  return (
    <div className="tc-page">
      <div className="tc-hero">
        <h1>{competition ? competition.name : 'Transformers Pinball Championship'}</h1>
        <p>
          {!competition && "Season setup - start Week 1 when you're ready."}
          {competition && competition.status === 'pending' && "Not started yet."}
          {competition && currentRound && `Week ${currentRound.round_number} · Event ${currentRound.stern_event_code}`}
          {competition && !currentRound && competition.status !== 'pending' && 'No active week'}
        </p>
      </div>

      {error && <div className="tc-danger-box">{error}</div>}

      {competition && (
        <div className="tc-section">
          <div className="tc-section-header-row">
            <h2>Public TV Dashboard</h2>
          </div>
          <p className="tc-form-hint">
            No-login page meant to run continuously on a screen in the club.
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" className="tc-btn tc-btn-primary" onClick={handleOpenTv}>
              Open TV Dashboard &#8599;
            </button>
            <button type="button" className="tc-btn tc-btn-secondary" onClick={handleCopyTvLink}>
              Copy TV Link
            </button>
          </div>
        </div>
      )}

      {competition && weeklyRecap && (
        <div className="tc-section">
          <div className="tc-section-header-row">
            <h2>Weekly Recap - Week {weeklyRecap.roundNumber}</h2>
            <button type="button" className="tc-btn tc-btn-primary tc-btn-sm" onClick={handleCopyRecap}>
              {recapCopied ? 'Copied!' : 'Copy for Discord'}
            </button>
          </div>
          <p className="tc-form-hint">
            Ready-to-paste summary of the most recently closed week. Fixed format, computed
            straight from the results, edit after pasting if you want to add anything.
          </p>
          <pre className="tc-recap-preview">{formatRecapText(weeklyRecap)}</pre>
        </div>
      )}

      {!competition && (
        <div className="tc-empty-state">
          <h2>No season in progress</h2>
          <p>Create the season, then start Week 1 when you're ready.</p>
          <button type="button" className="tc-btn tc-btn-primary" onClick={() => setShowCreateSheet(true)}>
            Create Season
          </button>
        </div>
      )}

      {competition && (
        <div className="tc-section">
          <div className="tc-section-header-row">
            <h2>Season Schedule</h2>
            <button type="button" className="tc-btn tc-btn-secondary tc-btn-sm" onClick={() => { setScheduleCodesText(''); setShowScheduleSheet(true); }}>
              Paste Event Codes
            </button>
          </div>
          <p className="tc-form-hint">
            Pre-load every week's Stern leaderboard ID up front (paste all 13 at once, one per
            line, in week order) and weeks advance automatically every Friday at 2:00 AM Eastern
            using whichever average carried forward from the week before - no need to be at your
            phone to create the next week's leaderboard.
          </p>
          {scheduledWeeks.length > 0 ? (
            <div className="tc-roster-list">
              {scheduledWeeks.map(w => (
                <div key={w.roundNumber} className="tc-roster-row">
                  {editingWeekNumber === w.roundNumber ? (
                    <form className="tc-inline-edit-form" onSubmit={handleSaveWeekEdit} style={{ display: 'flex', gap: 8, flex: 1, alignItems: 'center' }}>
                      <span className="tc-roster-player-name" style={{ whiteSpace: 'nowrap' }}>Week {w.roundNumber}</span>
                      <input
                        type="text"
                        className="tc-inline-input"
                        value={editingWeekCode}
                        onChange={(e) => setEditingWeekCode(e.target.value)}
                        autoFocus
                        required
                        style={{ flex: 1, minWidth: 0 }}
                      />
                      <button type="submit" className="tc-btn tc-btn-primary tc-btn-sm" disabled={savingWeekEdit}>
                        {savingWeekEdit ? 'Saving...' : 'Save'}
                      </button>
                      <button type="button" className="tc-btn tc-btn-secondary tc-btn-sm" onClick={() => setEditingWeekNumber(null)}>
                        Cancel
                      </button>
                    </form>
                  ) : (
                    <>
                      <div className="tc-roster-player">
                        <span className="tc-roster-player-name">Week {w.roundNumber}</span>
                        <span className="tc-roster-player-sub">{w.sternEventCode}</span>
                      </div>
                      <div className="tc-roster-metrics">
                        <button type="button" className="tc-btn tc-btn-secondary tc-btn-sm" onClick={() => openSternLeaderboard(w)}>
                          Leaderboard &#8599;
                        </button>
                        <button type="button" className="tc-btn tc-btn-secondary tc-btn-sm" onClick={() => startEditingWeek(w)}>
                          Edit
                        </button>
                        <button type="button" className="tc-btn tc-btn-danger tc-btn-sm" onClick={() => handleRemoveScheduledWeek(w.roundNumber)}>
                          Remove
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p>No weeks scheduled yet.</p>
          )}
        </div>
      )}

      {competition && competition.status === 'pending' && (
        <div className="tc-section">
          <div className="tc-section-header-row">
            <h2>{competition.name}</h2>
            <StatusBadge variant="neutral" uppercase>Not started</StatusBadge>
          </div>

          <div className="tc-info-box">
            No roster to build - anyone with a Stern username on their account is enrolled
            automatically and balanced onto the smallest team, both right now
            {eligibleCount !== null && ` (currently ${eligibleCount} member${eligibleCount === 1 ? '' : 's'})`}
            {' '}and continuously as new members add a Stern username later.
          </div>

          <button type="button" className="tc-btn tc-btn-primary tc-btn-block" style={{ marginTop: 16 }} onClick={() => setShowStartSheet(true)}>
            Start Season &rarr;
          </button>
        </div>
      )}

      {competition && (
        <div className="tc-section">
          <div className="tc-section-header-row">
            <h2>Testing Tools</h2>
          </div>
          <p className="tc-form-hint">
            Wipes "{competition.name}" and every week, player, and score attached to it. Since
            there's only ever one season, this is how you reset and start over while testing.
          </p>
          <button type="button" className="tc-btn tc-btn-danger tc-btn-block" onClick={handleDelete}>
            Reset Competition - Delete Everything &amp; Start Fresh
          </button>
        </div>
      )}

      {competition && competition.status !== 'pending' && (
        <>
          {competition.status === 'pending_tie_break' && (
            <div className="tc-warning-box">
              <strong>Grand Champion tie needs resolution.</strong> {competition.tie_break_note}
              <div style={{ marginTop: 8 }}>
                <button type="button" className="tc-btn tc-btn-primary tc-btn-sm" onClick={() => setShowTieBreakSheet(true)}>
                  Resolve Now
                </button>
              </div>
            </div>
          )}

          {competition.status === 'completed' && (competition.winner_user_id || competition.winner_stern_username) && (
            <div className="tc-success-box">
              Grand Champion: {competition.winner_user_id
                ? `${competition.winner_first_name} ${competition.winner_last_name}`
                : `${competition.winner_stern_username} (no account linked yet)`}
            </div>
          )}

          {currentRound && competition.status === 'active' && (
            <div className="tc-section">
              <div className="tc-section-header-row">
                <h2>Live Week {currentRound.round_number}</h2>
                <button type="button" className="tc-btn tc-btn-secondary tc-btn-sm" onClick={handleSync} disabled={syncing}>
                  {syncing ? 'Syncing...' : 'Re-sync scores'}
                </button>
              </div>
              <p className="tc-form-hint">
                Current average: {currentRound.machine_average_score ? formatScoreAbbreviated(currentRound.machine_average_score) : ' - '}
                {' '}(feeds everyone's target this week - carried forward from last week unless corrected)
              </p>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button type="button" className="tc-btn tc-btn-secondary" onClick={openAverageSheet}>
                  Update This Week's Average
                </button>
                <button type="button" className="tc-btn tc-btn-primary" onClick={() => setShowCloseSheet(true)}>
                  Close Week & Start Next &rarr;
                </button>
              </div>
            </div>
          )}

          <div className="tc-tabs">
            {TABS.map(tab => (
              <button
                key={tab.key}
                type="button"
                className={`tc-tab-btn ${activeTab === tab.key ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'roster' && (
            <div className="tc-section">
              <div className="tc-section-header-row">
                <h2>Roster ({players.length})</h2>
                <button type="button" className="tc-btn tc-btn-secondary tc-btn-sm" onClick={() => setSortByBatteryAsc(!sortByBatteryAsc)}>
                  Sort: {sortByBatteryAsc ? 'Battery ↑' : 'Team'}
                </button>
              </div>
              <p className="tc-form-hint">
                Any Stern username that shows up on the leaderboard is enrolled automatically and
                balanced onto the smallest team, whether or not it's linked to a club member
                account yet - there's nothing to add manually here. Unlinked players can still be
                claimed later by entering this exact username on a member's My Status page.
              </p>

              <div className="tc-roster-list">
                {sortedPlayers.map(player => {
                  const playerKey = player.userId || player.sternUsername;
                  const live = liveScoresByPlayerKey[playerKey];
                  return (
                    <div key={playerKey} className={`tc-roster-row ${player.isStasisLocked ? 'is-locked' : ''}`}>
                      <div className="tc-roster-player">
                        <span className="tc-roster-player-name">{getDisplayName(player)}</span>
                        <span className="tc-roster-player-sub">
                          {live ? `Score: ${formatScoreAbbreviated(live.score)} / Target: ${formatScoreAbbreviated(live.targetWithRust)}` : (player.sternUsername || 'No Stern username')}
                        </span>
                      </div>
                      <div className="tc-roster-metrics">
                        <span className={`tc-team-badge team-${player.team}`}><TeamIcon teamId={player.team} size={16} /> {getTeamMeta(player.team).name}</span>
                        {!player.isClaimed && <span className="tc-stasis-badge">Unlinked</span>}
                        <span className="tc-rust-badge">Rust {player.rustPoints}</span>
                        <BatteryMeter battery={player.energonBattery} isStasisLocked={player.isStasisLocked} />
                        <button
                          type="button"
                          className="tc-btn tc-btn-secondary tc-btn-sm"
                          onClick={() => openOverride(player)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="tc-btn tc-btn-danger tc-btn-sm"
                          onClick={() => handleRemovePlayer(player)}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  );
                })}
                {players.length === 0 && <p>No players enrolled yet.</p>}
              </div>
            </div>
          )}

          {activeTab === 'teams' && (
            <div className="tc-section">
              <h2>Team Standings {currentRound ? `(Week ${currentRound.round_number})` : ''}</h2>
              <div className="tc-team-standings">
                {(standings?.teams || [...TEAMS].map(t => ({ ...t, energonTotal: 0 })))
                  .slice()
                  .sort((a, b) => (b.energonTotal || 0) - (a.energonTotal || 0))
                  .map((team, index) => {
                    const maxTotal = Math.max(1, ...(standings?.teams || []).map(t => t.energonTotal || 0));
                    return (
                      <div key={team.id} className={`tc-team-standing-row ${team.isWeeklyWinner ? 'is-winner' : ''}`}>
                        <span className="tc-team-standing-rank">#{index + 1}</span>
                        <span className={`tc-team-badge team-${team.id}`}><TeamIcon teamId={team.id} size={16} /> {team.name}</span>
                        <div className="tc-team-standing-bar-track">
                          <div
                            className="tc-team-standing-bar-fill"
                            style={{ width: `${Math.max(2, ((team.energonTotal || 0) / maxTotal) * 100)}%`, background: getTeamMeta(team.id).color }}
                          />
                        </div>
                        <span className="tc-team-standing-value">{formatPercent(team.energonTotal || 0, 1)}</span>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}

          {activeTab === 'history' && (
            <div className="tc-section">
              <h2>Week History</h2>
              <div className="tc-roster-list">
                {rounds.slice().reverse().map(round => (
                  <div key={round.id} className="tc-roster-row">
                    <div className="tc-roster-player">
                      <span className="tc-roster-player-name">Week {round.round_number}</span>
                      <span className="tc-roster-player-sub">
                        Event {round.stern_event_code}
                        {round.machine_average_score && ` · Avg ${formatScoreAbbreviated(round.machine_average_score)}`}
                      </span>
                    </div>
                    <StatusBadge variant={round.status === 'completed' ? 'info' : 'success'} uppercase>{round.status}</StatusBadge>
                  </div>
                ))}
                {rounds.length === 0 && <p>No weeks yet.</p>}
              </div>
            </div>
          )}
        </>
      )}

      {showCreateSheet && (
        <div className="tc-sheet-overlay" onClick={() => setShowCreateSheet(false)}>
          <form className="tc-sheet" onClick={(e) => e.stopPropagation()} onSubmit={handleCreate}>
            <div className="tc-sheet-header">
              <h2>Create Season</h2>
              <button type="button" className="tc-sheet-close" onClick={() => setShowCreateSheet(false)}>&times;</button>
            </div>
            <div className="tc-form-group">
              <label>Season name</label>
              <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)} required />
            </div>
            <div className="tc-form-group">
              <label>Description (optional)</label>
              <input type="text" value={newDescription} onChange={(e) => setNewDescription(e.target.value)} />
            </div>
            <button type="submit" className="tc-btn tc-btn-primary tc-btn-block" disabled={creating}>
              {creating ? 'Creating...' : 'Create'}
            </button>
          </form>
        </div>
      )}

      {showStartSheet && (
        <div className="tc-sheet-overlay" onClick={() => setShowStartSheet(false)}>
          <form className="tc-sheet" onClick={(e) => e.stopPropagation()} onSubmit={handleStartSeason}>
            <div className="tc-sheet-header">
              <h2>Start Season - Week 1</h2>
              <button type="button" className="tc-sheet-close" onClick={() => setShowStartSheet(false)}>&times;</button>
            </div>
            <div className="tc-form-group">
              <label>Week 1 Stern leaderboard/event ID</label>
              <input type="text" value={sternEventCode} onChange={(e) => setSternEventCode(e.target.value)} placeholder="Leave blank to use Week 1's code from the schedule" />
              <span className="tc-form-hint">Only needed if you haven't already pasted a season schedule with Week 1's code in it.</span>
            </div>
            <div className="tc-form-group">
              <label>Machine's current average score</label>
              <input type="number" step="1" min="1" value={machineAverageScore} onChange={(e) => setMachineAverageScore(e.target.value)} required />
              <span className="tc-form-hint">
                Read this off the Transformers machine right now. Everyone's Week 1 target will be set to exactly this number.
              </span>
            </div>
            <button type="submit" className="tc-btn tc-btn-primary tc-btn-block" disabled={starting}>
              {starting ? 'Starting...' : 'Start Season'}
            </button>
          </form>
        </div>
      )}

      {showCloseSheet && (
        <div className="tc-sheet-overlay" onClick={() => setShowCloseSheet(false)}>
          <form className="tc-sheet" onClick={(e) => e.stopPropagation()} onSubmit={handleCloseWeek}>
            <div className="tc-sheet-header">
              <h2>Close Week {currentRound?.round_number} & Start Next</h2>
              <button type="button" className="tc-sheet-close" onClick={() => setShowCloseSheet(false)}>&times;</button>
            </div>
            <p className="tc-form-hint">
              Uses whichever average is already on this week (see "Update This Week's Average" if
              it needs correcting first) and carries it forward as next week's starting point.
            </p>
            <div className="tc-form-group">
              <label>Next week's Stern leaderboard/event ID</label>
              <input type="text" value={nextEventCode} onChange={(e) => setNextEventCode(e.target.value)} placeholder="Leave blank to use the scheduled code" />
              <span className="tc-form-hint">Only needed if the next week isn't already in the season schedule, or the season should end here.</span>
            </div>
            <button type="submit" className="tc-btn tc-btn-primary tc-btn-block" disabled={closing}>
              {closing ? 'Closing...' : 'Close Week & Start Next'}
            </button>
          </form>
        </div>
      )}

      {showScheduleSheet && (
        <div className="tc-sheet-overlay" onClick={() => setShowScheduleSheet(false)}>
          <form className="tc-sheet" onClick={(e) => e.stopPropagation()} onSubmit={handleSaveSchedule}>
            <div className="tc-sheet-header">
              <h2>Paste Event Codes</h2>
              <button type="button" className="tc-sheet-close" onClick={() => setShowScheduleSheet(false)}>&times;</button>
            </div>
            <div className="tc-form-group">
              <label>One Stern event code per line, in week order</label>
              <textarea
                rows={8}
                value={scheduleCodesText}
                onChange={(e) => setScheduleCodesText(e.target.value)}
                placeholder={'week1code\nweek2code\nweek3code\n...'}
                required
              />
              <span className="tc-form-hint">
                Line 1 becomes Week 1, line 2 becomes Week 2, and so on. Blank lines are skipped
                and won't overwrite a code already set for that week - to add just weeks 5-13,
                leave lines 1-4 blank.
              </span>
            </div>
            <button type="submit" className="tc-btn tc-btn-primary tc-btn-block" disabled={savingSchedule}>
              {savingSchedule ? 'Saving...' : 'Save Schedule'}
            </button>
          </form>
        </div>
      )}

      {showAverageSheet && (
        <div className="tc-sheet-overlay" onClick={() => setShowAverageSheet(false)}>
          <form className="tc-sheet" onClick={(e) => e.stopPropagation()} onSubmit={handleSaveAverage}>
            <div className="tc-sheet-header">
              <h2>Update Week {currentRound?.round_number}'s Average</h2>
              <button type="button" className="tc-sheet-close" onClick={() => setShowAverageSheet(false)}>&times;</button>
            </div>
            <div className="tc-form-group">
              <label>Machine average score</label>
              <input type="number" step="1" min="1" value={averageValue} onChange={(e) => setAverageValue(e.target.value)} required />
              <span className="tc-form-hint">Recalculates every player's target for this week immediately. Nothing else is affected.</span>
            </div>
            <button type="submit" className="tc-btn tc-btn-primary tc-btn-block" disabled={savingAverage}>
              {savingAverage ? 'Saving...' : 'Update Average'}
            </button>
          </form>
        </div>
      )}

      {overridePlayer && (
        <div className="tc-sheet-overlay" onClick={() => setOverridePlayer(null)}>
          <form className="tc-sheet" onClick={(e) => e.stopPropagation()} onSubmit={handleSaveOverride}>
            <div className="tc-sheet-header">
              <h2>Edit {getDisplayName(overridePlayer)}</h2>
              <button type="button" className="tc-sheet-close" onClick={() => setOverridePlayer(null)}>&times;</button>
            </div>
            <p>Current: {formatPercent(overridePlayer.energonBattery)} battery, {overridePlayer.rustPoints} rust{overridePlayer.isStasisLocked ? ', Stasis Locked' : ''}</p>
            <div className="tc-form-group">
              <label>Battery change (+/-)</label>
              <input type="number" step="0.1" value={overrideBatteryDelta} onChange={(e) => setOverrideBatteryDelta(e.target.value)} />
            </div>
            <div className="tc-form-group">
              <label>Rust change (+/-)</label>
              <input type="number" step="1" value={overrideRustDelta} onChange={(e) => setOverrideRustDelta(e.target.value)} />
            </div>
            <div className="tc-form-group">
              <label>Stasis Lock status</label>
              <select value={overrideForceLock} onChange={(e) => setOverrideForceLock(e.target.value)}>
                <option value="">No change</option>
                <option value="true">Force locked</option>
                <option value="false">Force unlocked</option>
              </select>
            </div>
            <button type="submit" className="tc-btn tc-btn-primary tc-btn-block" disabled={savingOverride}>
              {savingOverride ? 'Saving...' : 'Save Override'}
            </button>
          </form>
        </div>
      )}

      {showTieBreakSheet && (
        <div className="tc-sheet-overlay" onClick={() => setShowTieBreakSheet(false)}>
          <form className="tc-sheet" onClick={(e) => e.stopPropagation()} onSubmit={handleResolveTie}>
            <div className="tc-sheet-header">
              <h2>Resolve Grand Champion Tie</h2>
              <button type="button" className="tc-sheet-close" onClick={() => setShowTieBreakSheet(false)}>&times;</button>
            </div>
            <p className="tc-form-hint">{competition?.tie_break_note}</p>
            <div className="tc-form-group">
              <label>Declare Grand Champion</label>
              <select value={tieBreakWinnerId} onChange={(e) => setTieBreakWinnerId(e.target.value)} required>
                <option value="">Select a player...</option>
                {players.map(p => {
                  const key = p.userId ? String(p.userId) : `stern:${p.sternUsername}`;
                  return <option key={key} value={key}>{getDisplayName(p)}</option>;
                })}
              </select>
            </div>
            <div className="tc-form-group">
              <label>Note (optional)</label>
              <input type="text" value={tieBreakNote} onChange={(e) => setTieBreakNote(e.target.value)} />
            </div>
            <button type="submit" className="tc-btn tc-btn-primary tc-btn-block" disabled={resolvingTie}>
              {resolvingTie ? 'Saving...' : 'Declare Grand Champion'}
            </button>
          </form>
        </div>
      )}

      {confirmState && (
        <ConfirmModal
          title={confirmState.title}
          message={confirmState.message}
          confirmLabel={confirmState.confirmLabel}
          danger={confirmState.danger}
          submitting={confirmSubmitting}
          onConfirm={confirmState.onConfirm}
          onCancel={() => setConfirmState(null)}
        />
      )}

      <CompetitionBottomNav />
    </div>
  );
}

export default CompetitionAdmin;
